/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// react-router-dom ~/components

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material ~/components
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import Tooltip from "@mui/material/Tooltip";

// Material Dashboard 2 React ~/components
import { useSelector } from "react-redux";
import noPreview from "~/assets/images/no-picture.png";
import MDAvatar from "~/components/MDAvatar";
import MDBox from "~/components/MDBox";
import MDButton from "~/components/MDButton";
import MDTypography from "~/components/MDTypography";
import "../../MiniDetailsWaveCard/index.css";
import LocationOnIcon from '@mui/icons-material/LocationOn'; // Import the icon component
import MapIcon from '@mui/icons-material/Map';
import { IconButton } from "@mui/material";
import { useState } from "react";
import DialogBox from "~/components/DialogBox";

function DefaultProjectCard({
  image,
  label,
  title,
  description,
  action,
  authors,
  route,
  index,
  lat,
  long,
  plantColor,
  staticsData,
  plantationData
}) {
  const [isHovered, setIsHovered] = useState(false); // State to manage hover effect
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const clickedCardIndex = useSelector((store) => store.utils?.clickedCards);

  const renderAuthors = authors.map(({ image: media, name }) => (
    <Tooltip key={name} title={name} placement="bottom">
      <MDAvatar
        src={media}
        alt={name}
        size="xs"
        sx={({ borders: { borderWidth }, palette: { white } }) => ({
          border: `${borderWidth[2]} solid ${white.main}`,
          cursor: "pointer",
          position: "relative",
          ml: -1.25,

          "&:hover, &:focus": {
            zIndex: "10",
          },
        })}
      />
    </Tooltip>
  ));

  const coordinatesArray = [
    ...(Array.isArray(plantationData) ? plantationData.map(location => ({
      Latitude: parseFloat(location.Latitude),
      Longitude: parseFloat(location.Longitude),
      Altitude_meter: location.Altitude_meter || '', // Provide a default value or leave it empty as per your requirement
      Location: location.BarCode_Serial_No || null, // Provide a default value or leave it null as per your requirement
      qualitativeMonitoringData: location.qualitativeMonitoringData !== null ? location.qualitativeMonitoringData : "",
      quantativeMonitoringData: location.quantitativeMonitoringData !== null ? location.quantitativeMonitoringData: "null",
      plantColor: plantColor,
      plantTationCount: 0
    })) : []),
    {
      Latitude: parseFloat(lat), // Parse lat as a float
      Longitude: parseFloat(long), // Parse long as a float
      Altitude_meter: '', // Provide a default value or leave it empty as per your requirement
      Location: title, // Provide a default value or leave it null as per your requirement
      plantTationCount: staticsData,
      plantColor: plantColor,
      value: "plantation"
    }
  ];

  return (
    <>
      <Card
        className={`${clickedCardIndex[route] === index ? "bounce" : ""}`}
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: "transparent",
          boxShadow: "none",
          overflow: "visible",
        }}
      >
        <MDBox position="relative" width="100.25%" shadow="xl" borderRadius="xl">
          <CardMedia
            src={image ?? noPreview}
            component="img"
            title={title}
            sx={{
              width: "100%",
              height: "220px",
              margin: 0,
              boxShadow: ({ boxShadows: { md } }) => md,
              objectFit: "cover",
              objectPosition: "center",
            }}
          />
        </MDBox>
        <MDBox mt={1} mx={0.5}>
          <MDTypography
            variant="button"
            fontWeight="regular"
            color="text"
            textTransform="capitalize"
          >
            {label}
          </MDTypography>
          <MDBox mb={1} sx={{ display: 'flex', alignItems: 'center' }}>
            {action.type === "internal" ? (
              <MDTypography
                // component={Link}
                // to={action.route}
                variant="h5"
                textTransform="capitalize"
                sx={{ flex: 1 }} // Make the title take up remaining space
              >
                {title}
              </MDTypography>
            ) : (
              <MDTypography
                component="a"
                href={action.route}
                target="_blank"
                rel="noreferrer"
                variant="h5"
                textTransform="capitalize"
                sx={{ flex: 1 }} // Make the title take up remaining space
              >
                {title}
              </MDTypography>
            )}
            {coordinatesArray.length > 1 &&
            <IconButton
              size="medium"
              style={{ cursor: 'pointer', color: isHovered ? 'red' : '' }} // Set color based on hover state
              onMouseEnter={() => setIsHovered(true)} // Set hover state to true on mouse enter
              onMouseLeave={() => setIsHovered(false)} // Set hover state to false on mouse leave
              onClick={(e) => {
                e.stopPropagation(); // Stop event propagation
                setIsDialogOpen(true);
              }}
            >
              <LocationOnIcon />
            </IconButton>
}
          </MDBox>
          <MDBox mb={3} lineHeight={0}>
            <MDTypography variant="button" fontWeight="light" color="text">
              {description}
            </MDTypography>
          </MDBox>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {action.type === "internal" ? (
              <MDButton
                // component={Link}
                // to={action.route}
                variant="outlined"
                size="small"
                color={action.color}
              >
                {action.label}
              </MDButton>
            ) : null}
            <MDBox display="flex">{renderAuthors}</MDBox>
          </MDBox>
        </MDBox>
      </Card>
      {coordinatesArray.length > 1 &&
      <DialogBox open={isDialogOpen} onClose={() => setIsDialogOpen(false)} coordinates={coordinatesArray} />}
    </>
  );
}

// Setting default values for the props of DefaultProjectCard
DefaultProjectCard.defaultProps = {
  authors: [],
};

// Typechecking props for the DefaultProjectCard
DefaultProjectCard.propTypes = {
  image: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  plantColor: PropTypes.string.isRequired,
  description: PropTypes.any.isRequired,

  lat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  long: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,

  staticsData: PropTypes.number.isRequired,

  action: PropTypes.shape({
    type: PropTypes.oneOf(["external", "internal"]),
    route: PropTypes.string,
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "light",
      "dark",
      "white",
    ]).isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,

  authors: PropTypes.arrayOf(PropTypes.object),

  route: PropTypes.string.isRequired,

  // FIXED: index should accept number or string
  index: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  // FIXED: plantationData is array NOT object
  plantationData: PropTypes.array,
};

export default DefaultProjectCard;
